import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { DataTable, type ColumnDef, type ActionDef } from "@/components/data-table/DataTable";
import { BlueButton } from "@/components/ui/Buttons";
import { usePlatformAPI } from "@/api/PlatformProvider";

type Partner = {
    id: string;
    name: string;
    description?: string;
};

type PartnerApp = {
    id: string;
    name: string;
    description?: string;
    notificationEmail: string;
    requireSigning: boolean;
    rollAfterDays?: number;
    rolloverDays?: number;
    enabled: boolean;
    createdAt: string;
};

const columns: ColumnDef<PartnerApp>[] = [
    { key: "name", label: "Name", sortable: true, width: "1fr", required: true },
    { key: "notificationEmail", label: "Notification Email", width: "1fr" },
    {
        key: "enabled",
        label: "Status",
        width: "100px",
        render: (v) => (
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${v ? "bg-desaturated-green text-dark-green" : "bg-desaturated-red text-dark-red"}`}>
                {v ? "Enabled" : "Disabled"}
            </span>
        ),
    },
    {
        key: "createdAt",
        label: "Created",
        sortable: true,
        width: "140px",
        render: (v) => new Date(v as string).toLocaleDateString(),
    },
];

function PartnerAppsPage() {
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const api = usePlatformAPI();

    const from = searchParams.get("from") ?? "/partners";
    const [partner, setPartner] = useState<Partner | null>(null);
    const [showFullDescription, setShowFullDescription] = useState(false);

    const DESCRIPTION_PREVIEW = 300;

    useEffect(() => {
        api.get<Partner>(`api/partners/${id}`).then(([data, err]) => {
            if (!err) setPartner(data);
        });
    }, [api, id]);

    const appURL = (app: PartnerApp) => `/partners/${id}/apps/${app.id}`;
    const createURL = `/partners/${id}/apps/create?from=${encodeURIComponent(`/partners/${id}`)}`;

    const getItems = useCallback(async (params: {
        search: string;
        page: number;
        limit: number;
        orderBy?: { field: string; direction: "asc" | "desc" };
    }) => {
        const query: Record<string, unknown> = { page: params.page, limit: params.limit };
        if (params.search) query.search = params.search;
        if (params.orderBy) {
            query.orderBy = params.orderBy.field;
            query.orderByDir = params.orderBy.direction;
        }
        const [data, err] = await api.get<{ items: PartnerApp[]; total: number }>(`api/partners/${id}/apps`, query);
        if (err) throw err;
        return { items: data.items, total: data.total };
    }, [api, id]);

    const actions: ActionDef<PartnerApp>[] = [
        {
            label: "View",
            onClick: (a) => { navigate(appURL(a)); },
        },
        {
            label: (a) => a.enabled ? "Disable" : "Enable",
            onClick: async (a) => {
                const [, err] = await api.post(`api/partners/${id}/apps/${a.id}/toggle-enabled`);
                if (err) throw err;
            },
            optimisticUpdate: (a) => ({ ...a, enabled: !a.enabled }),
            confirm: {
                title: (a) => a.enabled ? `Disable "${a.name}"?` : `Enable "${a.name}"?`,
                description: (a) => a.enabled
                    ? "This will deactivate the app and suspend its access. You can re-enable it at any time."
                    : "This will reactivate the app and restore its access.",
                confirmLabel: "Confirm",
                variant: "warning",
            },
        },
        {
            label: "Delete",
            onClick: async (a) => {
                const [, err] = await api.delete(`api/partners/${id}/apps/${a.id}`);
                if (err) throw err;
            },
            confirm: {
                title: (a) => `Delete "${a.name}"?`,
                description: "This will permanently delete the app and all associated credentials. This action cannot be undone.",
                confirmLabel: "Delete",
                variant: "danger",
            },
        },
    ];

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-row items-center gap-4">
                <button onClick={() => navigate(from)} className="text-grey hover:text-dark-dark-grey flex items-center gap-1">
                    <ArrowBackIcon fontSize="small" />
                </button>
                <h1 className="flex items-center gap-2 text-xl">
                    <span className="font-semibold">{partner?.name ?? "Apps"}</span>
                </h1>
                <div className="flex-1" />
                <div className="w-36">
                    <BlueButton text="Create App" onClick={() => navigate(createURL)} />
                </div>
            </div>
            {partner?.description && (
                <div className="bg-white rounded-md outline outline-2 outline-light-light-grey p-6 flex flex-col gap-3">
                    <h3 className="text-sm font-semibold text-dark-dark-grey">Description</h3>
                    <pre className="text-sm text-grey whitespace-pre-wrap font-sans">
                        {showFullDescription || partner.description.length <= DESCRIPTION_PREVIEW
                            ? partner.description
                            : partner.description.slice(0, DESCRIPTION_PREVIEW) + "…"}
                    </pre>
                    {partner.description.length > DESCRIPTION_PREVIEW && (
                        <button
                            onClick={() => setShowFullDescription(v => !v)}
                            className="text-xs text-light-grey hover:text-grey self-start"
                        >
                            {showFullDescription ? "Show less" : "Show more"}
                        </button>
                    )}
                </div>
            )}
            <DataTable<PartnerApp>
                id={`partner-apps-${id}`}
                columns={columns}
                getItems={getItems}
                limit={20}
                actions={actions}
                onRowClick={(a) => navigate(appURL(a))}
            />
        </div>
    );
}

export default PartnerAppsPage;
